import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Tooltip from '@material-ui/core/Tooltip';
import NoteIcon from '@material-ui/icons/Note';
import {makeValidate, TextField} from 'mui-rff';
import React, {useCallback, useMemo, useState} from 'react';
import {Form} from 'react-final-form';
import * as yup from 'yup';
import LoadingButton from '../../LoadingIndicator/LoadingButton';
import {Reason} from '../Utils/ReasonsReducer';
import {useScheduleContext} from '../Utils/ScheduleContextProvider';

const useStyles = makeStyles(theme => ({
    cancel: {
        color: theme.palette.grey['500'],
    },
}));

type Props = {
    reasons : Reason[] | null;
    employeeId : number;
    startOfWeek : string;
};

const schema = yup.object().shape({
    reason: yup.string().label('Reason').ensure().trim(),
}).required();

type FormData = yup.InferType<typeof schema>;
const validate = makeValidate(schema);

const ReasonButton : React.FC<Props> = ({reasons, employeeId, startOfWeek} : Props) => {
    const classes = useStyles();
    const scheduleContext = useScheduleContext();
    const [open, setOpen] = useState(false);

    const reason = useMemo(() => {
        if (!reasons) {
            return undefined;
        }

        return reasons.find(reason => reason.employeeId === employeeId && reason.startOfWeek === startOfWeek);
    }, [reasons, employeeId, startOfWeek]);
    const initialValues = useMemo(() => ({reason: reason?.reason || ''}), [reason]);

    const handleSubmit = useCallback(async (formData : FormData) => {
        const data = schema.cast(formData);
        await scheduleContext.updateReason(employeeId, startOfWeek, data.reason as string);
        setOpen(false);
    }, [scheduleContext, employeeId, startOfWeek]);

    if (reasons === null) {
        return (
            <CircularProgress color="inherit" size={14}/>
        );
    }

    return (
        <>
            <Tooltip title="Show reason">
                <IconButton size="small" color="inherit" onClick={() => setOpen(true)}>
                    <NoteIcon fontSize="inherit" color={reason?.reason ? 'inherit' : 'disabled'}/>
                </IconButton>
            </Tooltip>

            <Form
                subscription={{submitting: true, pristine: true}}
                onSubmit={handleSubmit}
                initialValues={initialValues}
                validate={validate}
                render={({handleSubmit, pristine, submitting}) => (
                    <Dialog
                        open={open}
                        onClose={(_event, reason) => {
                            if(reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                                setOpen(false);
                            }
                        }}
                        disableEscapeKeyDown={submitting}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            component: 'form',
                            onSubmit: handleSubmit,
                            // @ts-ignore Incomplete prop definition
                            noValidate: true,
                        }}
                    >
                        <DialogTitle>Reason</DialogTitle>
                        <DialogContent>
                            <TextField
                                variant="outlined"
                                label="Reason"
                                name="reason"
                                autoFocus
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setOpen(false)}
                                disabled={submitting}
                                className={classes.cancel}
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                type="submit"
                                color="primary"
                                loading={submitting}
                                disabled={pristine || submitting}
                            >
                                Save
                            </LoadingButton>
                        </DialogActions>
                    </Dialog>
                )}
            />
        </>
    );
};

export default ReasonButton;
